import { SuspendedBranchRowComponent } from './secure/components/suspended-branch-row/suspended-branch-row.component';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClientModule, provideHttpClient, withFetch } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthClientConfig, AuthConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import * as Sentry from '@sentry/angular';
import { environment } from '../environments/environment';
import { DemoMaterialModule } from './../material-module';
import { ConfirmDialogComponent } from './a2p/common/confirm-dialog/confirm-dialog.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCallbackComponent } from './public/auth/auth-callback/auth-callback.component';
import { LoginComponent } from './public/auth/login/login.component';
import { BranchMainListViewComponent } from './public/branch-main-list-view/branch-main-list-view.component';
import { BundleSummaryPurchaseComponent } from './public/bundle-summary-purchase/bundle-summary-purchase.component';
import { DailySnapshotThumbnailComponent } from './public/daily-snapshot-thumbnail/daily-snapshot-thumbnail.component';
import { AdminLocationActionsComponent } from './public/editable-location/admin-location-actions.component';
import { LocationPropertiesPopupComponent } from './public/location-properties-popup/location-properties-popup.component';
import { LoggedOutComponent } from './public/logged-out/logged-out.component';
import { ManageAccessPopupComponent } from './public/manage-access-popup/manage-access-popup.component';
import { OrgUuidFieldComponent } from './public/org-uuid-field/org-uuid-field.component';
import { OrgUUIDPopupComponent } from './public/org-uuid-popup/org-uuid-popup.component';
import { SessionExpiredPopupComponent } from './public/session-expired-popup/session-expired-popup.component';
import { StripePurchaseUiComponent } from './public/stripe-purchase-ui/stripe-purchase-ui.component';
import { StripeSubscriptionManagePageComponent } from './public/stripe-subscription-manage-card/stripe-subscription-manage-page.component';
import { AppFooterComponent } from './secure/components/app-footer/app-footer.component';
import { CancellationDetailsComponent } from './secure/components/cancellation-details/cancellation-details.component';
import { CommentInputComponent } from './secure/components/comment-input/comment-input.component';
import { CommentComponent } from './secure/components/comment/comment.component';
import { CompanyLogoComponent } from './secure/components/company-logo/company-logo.component';
import { MovementNoPurchaseCommentsComponent } from './secure/components/movement-no-purchase-comments/movement-no-purchase-comments.component';
import { MovementNoPurchaseDetailsComponent } from './secure/components/movement-no-purchase-details/movement-no-purchase-details.component';
import { MovementNoPurchaseResultMenuComponent } from './secure/components/movement-no-purchase-result-menu/movement-no-purchase-result-menu.component';
import { NotFoundComponent } from './secure/components/not-found/not-found.component';
import { SuspectImageComponent } from './secure/components/suspect-image/suspect-image.component';
import { TheftReportDatagridComponent } from './secure/components/theft-report-datagrid/theft-report-datagrid.component';
import { TransactionCommentsComponent } from './secure/components/transaction-comments/transaction-comments.component';
import { TransactionDetailsComponent } from './secure/components/transaction-details/transaction-details.component';
import { SecureHomeComponent } from './secure/landing/secure-home.component';
import { LogoutComponent } from './secure/logout/logout.component';
import { TheftReportComponent } from './secure/pages/theft-report/theft-report.component';
import { ImagePopupComponent } from './secure/popups/image-popup/image-popup.component';
import { MisidentifiedFaceCloseDialogComponent } from './secure/popups/misidentified-face-close-dialog/misidentified-face-close-dialog.component';
import { MovementNoPurchaseDetailsPopupComponent } from './secure/popups/movement-no-purchase-details-popup/movement-no-purchase-details-popup.component';
import { SoldDetailsComponent } from './secure/popups/sold-details/sold-details.component';
import { TransactionDetailsPopupComponent } from './secure/popups/transaction-details-popup/transaction-details-popup.component';
import { CustomerPortalComponent } from './secure/tabs/customer-portal/customer-portal.component';
import { NewLocationDialogComponent } from './secure/tabs/customer-portal/new-location-dialog/new-location-dialog.component';
import { CdkDetailRowDirective } from './secure/tabs/identities/cdk-detail-row.direcive';
import { IdentitiesComponent } from './secure/tabs/identities/identities.component';
import { LossPreventionAdminNavigationComponent } from './secure/tabs/loss-prevention/admin/navigation/navigation.component';
import { LossPreventionHomeComponent } from './secure/tabs/loss-prevention/loss-prevention-home/loss-prevention-home.component';
import { TransactionsTabComponent } from './secure/tabs/transactions/transactions-tab.component';
import { UncertainFacesComponent } from './secure/tabs/uncertain/uncertain-faces.component';
import { CustomerPortalService } from './service/customer-portal.service';
import { PanoptycAdminService } from './service/panoptyc-admin.service';
import { PanoptycDataService } from './service/panoptyc-data.service';
import { PosthogService } from './service/posthog.service';
import { TheftReportService } from './service/theft-report.service';
import { UserLoginService } from './service/user-login.service';

import { CurrencyPipe, NgOptimizedImage, SlicePipe } from '@angular/common';
import { FormField } from '@angular/forms/signals';
import { MatChipsModule } from '@angular/material/chips';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { AgGridAngular } from 'ag-grid-angular';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { filter } from 'rxjs/operators';
import { BlobToImagePipe } from './blobtoimage.pipe';
import { UiModule } from './components/ui.module';
import { CopyTextDirective } from './directives/copytext.directive';
import { ServerErrorsInterceptor } from './error-interceptor';
import { initializeAGGrid } from './helpers';
import { AdminUserManagementBranchComponent } from './public/admin-user-management-branch/admin-user-management-branch.component';
import { AdminUserManagementCreateUserComponent } from './public/admin-user-management-create-user/admin-user-management-create-user.component';
import { AdminUserManagementLocationDomainsWhitelistComponent } from './public/admin-user-management-location-domains-whitelist/admin-user-management-location-domains-whitelist.component';
import { AdminUserManagementLocationGroupsWhitelistComponent } from './public/admin-user-management-location-groups-whitelist/admin-user-management-location-groups-whitelist.component';
import { AdminUserManagementLocationComponent } from './public/admin-user-management-location/admin-user-management-location.component';
import { AdminUserManagementUserBranchesComponent } from './public/admin-user-management-userbranches/admin-user-management-userbranches.component';
import { AdminUserManagementUserLocationsComponent } from './public/admin-user-management-userlocations/admin-user-management-user-locations.component';
import { AdminUserManagementUsersComponent } from './public/admin-user-management-users/admin-user-management-users.component';
import { AdminUserManagementComponent } from './public/admin-user-management/admin-user-management.component';
import { BundleTransactionHistoryComponent } from './public/bundle-transaction-history/bundle-transaction-history.component';
import { ImagePreviewComponent } from './public/image-preview/image-preview.component';
import { ManageShrinkPageComponent } from './public/manage-shrink/manage-shrink.component';
import { StripePurchaseBundleUiComponent } from './public/stripe-purchase-bundle-ui/stripe-purchase-bundle-ui.component';
import { AppHeaderComponent } from './secure/components/app-header/app-header.component';
import { BillingPortalBranchCardComponent } from './secure/components/billing-portal-branch-card/billing-portal-branch-card.component';
import { BranchMergeModalComponent } from './secure/components/branch-merge-modal/branch-merge-modal.component';
import { BranchSplitModalComponent } from './secure/components/branch-split-modal/branch-split-modal.component';
import { DebugPanelComponent } from './secure/components/debug-panel/debug-panel.component';
import { DiscussionThreadComponent } from './secure/components/discussion-thread/discussion-thread.component';
import { ForbiddenComponent } from './secure/components/forbidden/forbidden.component';
import { IncidentTimelineTableComponent } from './secure/components/incident-timeline-table/incident-timeline-table.component';
import { OptionalExpansionPanelComponent } from './secure/components/optional-expansion-panel/optional-expansion-panel.component';
import { PageFiltersComponent } from './secure/components/page-filters/page-filters.component';
import { PageSelectBranchComponent } from './secure/components/page-select-branch/page-select-branch.component';
import { PageSelectLocationGroupComponent } from './secure/components/page-select-location-group/page-select-location-group.component';
import { PageSelectLocationComponent } from './secure/components/page-select-location/page-select-location.component';
import { ReportPortalBranchCardComponent } from './secure/components/report-portal-branch-card/report-portal-branch-card.component';
import { TheftNoticeAlertComponent } from './secure/components/theft-notice-alert/theft-notice-alert.component';
import { ReviewManagementBranchCardComponent } from './secure/components/review-management-branch-card/review-management-branch-card.component';
import { UserRoleSelectionComponent } from './secure/components/user-role-selection/user-role-selection.component';
import { NumericInputDirective } from './secure/directives/numeric-input.directive';
import { TrackReviewActivityDirective } from './secure/directives/track-review-activity.directive';
import { AssetRecoveryInvoiceModalComponent } from './secure/pages/asset-recovery-report/asset-recovery-invoice-modal/asset-recovery-invoice-modal.component';
import { AssetRecoveryReportComponent } from './secure/pages/asset-recovery-report/asset-recovery-report.component';
import { HomePageComponent } from './secure/pages/home/home-page.component';
import { RequestAccessPageComponent } from './secure/pages/request-access/request-access-page.component';
import { SharedInvestigationComponent } from './secure/pages/shared-investigation/shared-investigation.component';
import { UserPreferencesComponent } from './secure/pages/user-preferences/user-preferences.component';
import { UserReportComponent } from './secure/pages/user-report/user-report.component';
import { ApikeyManagementComponent } from './secure/tabs/apikey-management/apikey-management.component';
import { AssetRecoveryComponent } from './secure/tabs/asset-recovery/asset-recovery.component';
import { BlManagementBranchComponent } from './secure/tabs/bl-management-branch/bl-management-branch.component';
import { BlManagementBranchesComponent } from './secure/tabs/bl-management-branches/bl-management-branches.component';
import { BlManagementLocationComponent } from './secure/tabs/bl-management-location/bl-management-location.component';
import { BlManagementLocationGroupComponent } from './secure/tabs/bl-management-locationgroup/bl-management-locationgroup.component';
import { BlManagementLocationGroupsComponent } from './secure/tabs/bl-management-locationgroups/bl-management-locationgroups.component';
import { BlManagementLocationsComponent } from './secure/tabs/bl-management-locations/bl-management-locations.component';
import { BlManagementComponent } from './secure/tabs/bl-management/bl-management.component';
import { BranchBillingComponent } from './secure/tabs/branch-billing/branch-billing.component';
import { CameraEditComponent } from './secure/tabs/camera-edit/camera-edit.component';
import { CameraHealthComponent } from './secure/tabs/camera-health/camera-health.component';
import { CaseTrackingGuestPortalComponent } from './secure/tabs/case-tracking-guest-portal/case-tracking-guest-portal.component';
import { CaseTrackingPortalComponent } from './secure/tabs/case-tracking-portal/case-tracking-portal.component';
import { CaseShareHistoryComponent } from './secure/tabs/case-tracking-portal/share-history/case-share-history.component';
import { GroceryCasePortalComponent } from './secure/tabs/grocery-case-portal/grocery-case-portal.component';
import { GroceryCtpBranchStatisticsComponent } from './secure/tabs/grocery-case-portal/grocery-ctp-branch-statistics/grocery-ctp-branch-statistics.component';
import { IncidentSubmissionComponent } from './secure/tabs/incident-submission/incident-submission.component';
import { InstallerDashboardTableKiosksComponent } from './secure/tabs/installer-dashboard/installer-dashboard-table-kiosks/installer-dashboard-table-kiosks.component';
import { InstallerDashboardTableLocationsComponent } from './secure/tabs/installer-dashboard/installer-dashboard-table-locations/installer-dashboard-table-locations.component';
import { InstallerDashboardTablePanobridgeComponent } from './secure/tabs/installer-dashboard/installer-dashboard-table-panobridge/installer-dashboard-table-panobridge.component';
import { InstallerDashboardTableProvidersComponent } from './secure/tabs/installer-dashboard/installer-dashboard-table-providers/installer-dashboard-table-providers.component';
import { InstallerDashboardTableShoppersenseComponent } from './secure/tabs/installer-dashboard/installer-dashboard-table-shoppersense/installer-dashboard-table-shoppersense.component';
import { InstallerDashboardComponent } from './secure/tabs/installer-dashboard/installer-dashboard.component';
import { LocationChangeHistoryComponent } from './secure/tabs/location-change-history/location-change-history.component';
import { LocationProfileComponent } from './secure/tabs/location-profile/location-profile.component';
import { LossPreventionAdminCreateReviewComponent } from './secure/tabs/loss-prevention/admin/create-review/create-review.component';
import { CreateWalkoutComponent } from './secure/tabs/loss-prevention/admin/create-review/create-walkout.component';
import { LossPreventionAdminDashboardComponent } from './secure/tabs/loss-prevention/admin/dashboard/dashboard.component';
import { InspectAssignmentComponent } from './secure/tabs/loss-prevention/admin/inspect-assignment/inspect-assignment.component';
import { LossPreventionAdminInvestigationQueueComponent } from './secure/tabs/loss-prevention/admin/investigation-queue/investigation-queue.component';
import { LossPreventionAdminLocationInstructionsComponent } from './secure/tabs/loss-prevention/admin/location-instructions/location-instructions.component';
import { LossPreventionAdminManageQueueComponent } from './secure/tabs/loss-prevention/admin/manage-queue/manage-queue.component';
import { LossPreventionAdminQueuedAssignmentsComponent } from './secure/tabs/loss-prevention/admin/queued-assignments/queued-assignments.component';
import { LossPreventionAdminReviewPrioritiesComponent } from './secure/tabs/loss-prevention/admin/review-priorities/review-priorities.component';
import { LossPreventionAdminSideloadReviewComponent } from './secure/tabs/loss-prevention/admin/sideload-review/sideload-review.component';
import { UploadBranchAliasComponent } from './secure/tabs/loss-prevention/admin/upload-transactions/upload-branch-alias.component';
import { AdminUploadTransactionsComponent } from './secure/tabs/loss-prevention/admin/upload-transactions/upload-transactions.component';
import { GroceryIncidentFormComponent } from './secure/tabs/loss-prevention/grocery-incident-form/grocery-incident-form.component';
import { InvestigationAssignmentComponent } from './secure/tabs/loss-prevention/investigation-assignment/investigation-assignment.component';
import { ReviewCheckAssignmentComponent } from './secure/tabs/loss-prevention/review-check-assignment/review-check-assignment.component';
import { ReviewFormModule } from './secure/tabs/loss-prevention/review-submit-form/review-form.module';
import { ReviewTimerComponent } from './secure/tabs/loss-prevention/review-timer/review-timer.component';
import { SelfServiceAssignmentComponent } from './secure/tabs/loss-prevention/self-service-assignment/self-service-assignment.component';
import { SideloadedIncidentFormComponent } from './secure/tabs/loss-prevention/sideloaded-incident-form/sideloaded-incident-form.component';
import { TransactionIncidentFormComponent } from './secure/tabs/loss-prevention/transaction-incident-form/transaction-incident-form.component';
import { ValidationAssignmentComponent } from './secure/tabs/loss-prevention/validation-assignment/validation-assignment.component';
import { VideoPasswordActionsComponent } from './secure/tabs/loss-prevention/video-password-actions/video-password-actions.component';
import { WalkoutIncidentFormComponent } from './secure/tabs/loss-prevention/walkout-incident-form/walkout-incident-form.component';
import { MarketSnapshotDetailComponent } from './secure/tabs/market-snapshots/market-snapshot-detail/market-snapshot-detail.component';
import { MarketSnapshotsComponent } from './secure/tabs/market-snapshots/market-snapshots.component';
import { ReportDashboardComponent } from './secure/tabs/report-dashboard/report-dashboard.component';
import { ReportPortalComponent } from './secure/tabs/report-portal/report-portal.component';
import { ReviewManagementComponent } from './secure/tabs/review-management/review-management.component';
import { AssignmentService } from './service/assignment.service';
import { CustomerIOService } from './service/customerio.service';
import { DiscussionsService } from './service/discussions.service';
import { LossPreventionService } from './service/loss-prevention.service';
import { SignalrPresenceService } from './service/signalr-presence.service';
import { SignalrTransactionUploadsService } from './service/signalr-transaction-uploads.service';
import { ShareEmailModalComponent } from './share-modal/share-email-modal.component';
import { SharedHomeComponent } from './shared/landing/shared-home.component';
import { TruncatePipe } from './truncate.pipe';
import { Auth0Connections } from './types';
import { getAppRedirectUri, getSharedRedirectUri, isSharedPage } from './utils';

initializeAGGrid();

function panoptycUriMatcher(uri) {
  uri = uri.toLocaleLowerCase();
  const isKnownDomain =
    uri.indexOf(environment.apiBaseUrl.toLocaleLowerCase()) > -1 || uri.indexOf(environment.apiBaseUrl.toLocaleLowerCase()) > -1;
  return isKnownDomain && uri.indexOf('shared') === -1;
}

function panoptycUriMatcherAnonymous(uri: string) {
  uri = uri.toLocaleLowerCase();
  const isKnownDomain =
    uri.indexOf(environment.apiBaseUrl.toLocaleLowerCase()) > -1 || uri.indexOf(environment.apiBaseUrl.toLocaleLowerCase()) > -1;
  return (
    isKnownDomain &&
    (uri.indexOf('shared') !== -1 || uri.endsWith('/me') || uri.endsWith('/getuserconnection') || uri.endsWith('/request-access'))
  );
}

const authConfig: AuthConfig = {
  domain: environment.auth0Domain,
  clientId: environment.auth0ClientId,
  authorizationParams: {
    // Request this audience at user authentication time
    audience: environment.auth0Audience,
    redirect_uri: getAppRedirectUri(),
    // Request this scope at user authentication time
    // scope: 'read:current_user',
  },
  // Source: https://github.com/auth0/auth0-angular/issues/455#issuecomment-1643006377
  useRefreshTokens: true,
  cacheLocation: 'localstorage' as const,
  httpInterceptor: {
    allowedList: [
      // shared pages have optional authentication (controlled on backend)
      {
        uriMatcher: panoptycUriMatcherAnonymous,
        allowAnonymous: true,
      },

      // Attach access tokens to any calls to '/api' (exact match)
      {
        uriMatcher: panoptycUriMatcher,
      },
    ],
  },
};

/**
 * Handle page specific auth configuration
 */
const configInitializer = (_httpBackend: HttpBackend, router: Router, authClientConfig: AuthClientConfig) => () =>
  new Promise<boolean>((resolve) => {
    const updateConfig = () => {
      const config = { ...authConfig };
      // always use the OTP client id for shared pages
      if (isSharedPage()) {
        config.authorizationParams.redirect_uri = getSharedRedirectUri();
        config.authorizationParams.connection = Auth0Connections.Email;
      }
      authClientConfig.set(config);
    };
    updateConfig();
    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(updateConfig);

    resolve(true);
  });

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    IdentitiesComponent,
    CdkDetailRowDirective,
    SecureHomeComponent,
    SharedHomeComponent,
    LogoutComponent,
    LoggedOutComponent,
    TransactionDetailsPopupComponent,
    UncertainFacesComponent,
    CameraHealthComponent,
    ImagePopupComponent,
    MisidentifiedFaceCloseDialogComponent,
    TransactionDetailsComponent,
    SuspectImageComponent,
    SharedInvestigationComponent,
    CommentInputComponent,
    CommentComponent,
    TransactionCommentsComponent,
    AdminLocationActionsComponent,
    BranchMainListViewComponent,
    SessionExpiredPopupComponent,
    DailySnapshotThumbnailComponent,
    LocationPropertiesPopupComponent,
    TransactionsTabComponent,
    ManageAccessPopupComponent,
    OrgUUIDPopupComponent,
    SoldDetailsComponent,
    ConfirmDialogComponent,
    CustomerPortalComponent,
    NewLocationDialogComponent,
    AppFooterComponent,
    AppHeaderComponent,
    TheftReportComponent,
    TheftReportDatagridComponent,
    CompanyLogoComponent,
    OrgUuidFieldComponent,
    MovementNoPurchaseDetailsPopupComponent,
    MovementNoPurchaseDetailsComponent,
    MovementNoPurchaseCommentsComponent,
    NotFoundComponent,
    ForbiddenComponent,
    MovementNoPurchaseResultMenuComponent,
    AuthCallbackComponent,
    StripeSubscriptionManagePageComponent,
    BundleSummaryPurchaseComponent,
    BundleTransactionHistoryComponent,
    StripePurchaseUiComponent,
    CancellationDetailsComponent,
    ReportPortalComponent,
    StripePurchaseBundleUiComponent,
    ReviewCheckAssignmentComponent,
    TransactionIncidentFormComponent,
    SideloadedIncidentFormComponent,
    GroceryIncidentFormComponent,
    WalkoutIncidentFormComponent,
    ValidationAssignmentComponent,
    SelfServiceAssignmentComponent,
    InvestigationAssignmentComponent,
    ReviewTimerComponent,
    LossPreventionHomeComponent,
    LossPreventionAdminNavigationComponent,
    LossPreventionAdminReviewPrioritiesComponent,
    LossPreventionAdminDashboardComponent,
    InspectAssignmentComponent,
    LossPreventionAdminSideloadReviewComponent,
    LossPreventionAdminLocationInstructionsComponent,
    LossPreventionAdminLocationInstructionsComponent,
    LossPreventionAdminInvestigationQueueComponent,
    LossPreventionAdminQueuedAssignmentsComponent,
    LossPreventionAdminManageQueueComponent,
    LossPreventionAdminCreateReviewComponent,
    CreateWalkoutComponent,
    VideoPasswordActionsComponent,
    AdminUploadTransactionsComponent,
    UploadBranchAliasComponent,
    AdminUserManagementComponent,
    AdminUserManagementBranchComponent,
    AdminUserManagementLocationComponent,
    AdminUserManagementLocationDomainsWhitelistComponent,
    AdminUserManagementLocationGroupsWhitelistComponent,
    BlobToImagePipe,
    ImagePreviewComponent,
    InstallerDashboardComponent,
    RequestAccessPageComponent,
    ShareEmailModalComponent,
    CaseTrackingPortalComponent,
    CaseTrackingGuestPortalComponent,
    CaseShareHistoryComponent,
    GroceryCasePortalComponent,
    IncidentSubmissionComponent,
    OptionalExpansionPanelComponent,
    BlManagementLocationComponent,
    CameraEditComponent,
    UserPreferencesComponent,
    DebugPanelComponent,
    HomePageComponent,
    LocationChangeHistoryComponent,
    BranchBillingComponent,
    ReportPortalBranchCardComponent,
    MarketSnapshotsComponent,
    MarketSnapshotDetailComponent,
    ReviewManagementComponent,
    InstallerDashboardTableProvidersComponent,
    InstallerDashboardTableKiosksComponent,
    InstallerDashboardTableLocationsComponent,
    InstallerDashboardTableShoppersenseComponent,
    InstallerDashboardTablePanobridgeComponent,
    IncidentTimelineTableComponent,
    BlManagementComponent,
    BlManagementBranchesComponent,
    BlManagementBranchComponent,
    BlManagementLocationsComponent,
    BlManagementLocationGroupsComponent,
    BlManagementLocationGroupComponent,
    ManageShrinkPageComponent,
    LocationProfileComponent,
    ApikeyManagementComponent,
    AssetRecoveryComponent,
    BranchSplitModalComponent,
    BranchMergeModalComponent,
    TheftNoticeAlertComponent,
    ReportDashboardComponent,
    AssetRecoveryReportComponent,
    AssetRecoveryInvoiceModalComponent,
    UserReportComponent,
    BillingPortalBranchCardComponent,
    SuspendedBranchRowComponent,
  ],
  imports: [
    AppRoutingModule,
    RouterModule,
    BrowserModule,
    AuthModule.forRoot(),
    HttpClientModule,
    DemoMaterialModule,
    BrowserAnimationsModule,
    MatTableModule,
    MatNativeDateModule,
    MatChipsModule,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    NgOptimizedImage,
    BaseChartDirective,
    CopyTextDirective,
    UiModule,
    SweetAlert2Module.forRoot({
      provideSwal: () =>
        import('sweetalert2').then(({ default: swal }) =>
          swal.mixin({
            reverseButtons: true,
            confirmButtonColor: '#0069a8',
          }),
        ),
    }),
    AdminUserManagementUserBranchesComponent,
    AdminUserManagementUserLocationsComponent,
    AdminUserManagementUsersComponent,
    AgGridAngular,
    AdminUserManagementCreateUserComponent,
    TrackReviewActivityDirective,
    PageFiltersComponent,
    PageSelectBranchComponent,
    PageSelectLocationComponent,
    PageSelectLocationGroupComponent,
    NumericInputDirective,
    ReviewManagementBranchCardComponent,
    ReviewFormModule,
    DiscussionThreadComponent,
    UserRoleSelectionComponent,
    TruncatePipe,
    FormField,
    GroceryCtpBranchStatisticsComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [HttpBackend, Router, AuthClientConfig, Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (cioService: CustomerIOService) => {
        return () => cioService.init();
      },
      deps: [CustomerIOService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorsInterceptor, multi: true },
    CurrencyPipe,
    SlicePipe,
    UserLoginService,
    provideHttpClient(withFetch()),
    PanoptycDataService,
    PanoptycAdminService,
    CustomerPortalService,
    TheftReportService,
    LossPreventionService,
    AssignmentService,
    PosthogService,
    SignalrPresenceService,
    DiscussionsService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: SignalrTransactionUploadsService,
      useFactory: () => {
        const service = new SignalrTransactionUploadsService();
        service.connectTransactionUploadsHub();
        return service;
      },
      deps: [],
    },
    provideCharts(withDefaultRegisterables()),
  ],
})
export class AppModule {}
